/**
 * useContextData.ts
 *
 * Custom hook for fetching and managing context execution results.
 * Handles pagination, refresh with cache bypass, and loading/error states.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';
import { executeContext } from '../api/contextApi';
import type { SphereStatusPoint, SpherePoints } from 'types/context';

/** Sphere data returned by the context execution endpoint */
export interface SphereData {
  id: string;
  label?: string;
  schemaType?: string;
  /** Status point enrichment (when includeStatus is true) */
  status?: SphereStatusPoint;
  /** Calculated gravity/velocity metrics (when includePoints is true) */
  points?: SpherePoints;
  [key: string]: unknown;
}

/** Level summary from the execution response */
export interface LevelSummary {
  id: string;
  order: number;
  label: string;
  direction: string;
  totalCount: number;
}

/** Cache metadata from execution response */
export interface CacheMetadata {
  structureHit: boolean;
  dataHits: number;
  dataMisses: number;
}

/** Full execution response from graph-service */
export interface ContextExecutionResult {
  context: {
    id: string;
    systemName: string;
    name: Record<string, string>;
    levels: LevelSummary[];
  };
  spheresByColumn: Record<number, SphereData[]>;
  continuationTokens: Record<string, string>;
  stats: {
    totalLoaded: number;
    byColumn: Record<number, number>;
    executionTimeMs: number;
  };
  _cache: CacheMetadata;
}

/** Parameters for executing a context */
export interface UseContextDataParams {
  /** Context system name to execute */
  systemName: string;
  /** Root sphere ID for the query */
  rootSphereId: string;
  /** Number of results per page per level */
  limit?: number;
  /** Max depth for repeat-based levels */
  depth?: number;
  /** Include status point enrichment on each sphere */
  includeStatus?: boolean;
  /** Include calculated gravity/velocity points on each sphere */
  includePoints?: boolean;
}

/** Return value of the useContextData hook */
export interface UseContextDataReturn {
  /** Execution result data (null until first fetch completes) */
  data: ContextExecutionResult | null;
  /** Whether a fetch is in progress */
  loading: boolean;
  /** Error message if the last fetch failed */
  error: string | null;
  /** Cache metadata from the last successful fetch */
  cacheMetadata: CacheMetadata | null;
  /** Re-fetch with fresh data (bypasses all caches) */
  refresh: () => Promise<void>;
  /** Fetch the next page of results */
  fetchMore: () => Promise<void>;
  /** Whether more data is available to load */
  hasMore: boolean;
}

/**
 * Hook for consuming context execution results from graph-service.
 *
 * @param apiClients - API clients from ApiClientsContext (pass null if not ready)
 * @param params - Execution parameters (systemName, rootSphereId, etc.)
 */
export function useContextData(
  apiClients: ApiClients | null,
  params: UseContextDataParams
): UseContextDataReturn {
  const { systemName, rootSphereId, limit = 50, depth, includeStatus, includePoints } = params;

  const [data, setData] = useState<ContextExecutionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cacheMetadata, setCacheMetadata] = useState<CacheMetadata | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const isMountedRef = useRef(true);
  // Use a ref to track offset for fetchMore without causing re-renders
  const offsetRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(
    async (options: { reset?: boolean; skipCache?: boolean } = {}) => {
      const { reset = true, skipCache = false } = options;

      if (!apiClients || !systemName || !rootSphereId) return;

      if (reset) {
        setLoading(true);
        offsetRef.current = 0;
      }
      setError(null);

      try {
        const currentOffset = reset ? 0 : offsetRef.current;
        const response = await executeContext(apiClients.graphClient, systemName, {
          rootSphereId,
          limit,
          offset: currentOffset,
          depth,
          skipCache: skipCache || undefined,
          enrichmentOverrides: { includeStatus, includePoints },
        });

        if (!isMountedRef.current) return;

        if (reset) {
          setData(response);
        } else {
          // Merge: append spheres per column
          setData((prev) => {
            if (!prev) return response;
            const merged = { ...response };
            const mergedColumns: Record<number, SphereData[]> = { ...prev.spheresByColumn };
            for (const [col, spheres] of Object.entries(response.spheresByColumn)) {
              const colNum = Number(col);
              mergedColumns[colNum] = [
                ...(mergedColumns[colNum] || []),
                ...spheres,
              ];
            }
            merged.spheresByColumn = mergedColumns;
            merged.stats = {
              ...response.stats,
              totalLoaded: Object.values(mergedColumns).reduce(
                (sum, arr) => sum + arr.length,
                0
              ),
            };
            return merged;
          });
        }

        setCacheMetadata(response._cache);

        const newOffset = currentOffset + limit;
        offsetRef.current = newOffset;
        // If any column returned fewer results than limit, no more data
        const columnsReturned = Object.values(response.spheresByColumn);
        const anyFull = columnsReturned.some((arr) => arr.length >= limit);
        setHasMore(anyFull && columnsReturned.length > 0);
      } catch (err: unknown) {
        if (!isMountedRef.current) return;
        const message =
          err instanceof Error ? err.message : 'Failed to execute context';
        setError(message);
        if (reset) setData(null);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [apiClients, systemName, rootSphereId, limit, depth, includeStatus, includePoints]
  );

  // Initial fetch when params are ready - fetchData not in deps to avoid infinite loop
  useEffect(() => {
    if (apiClients && systemName && rootSphereId) {
      fetchData({ reset: true });
    }
  }, [apiClients, systemName, rootSphereId, limit, depth, fetchData]);

  const refresh = useCallback(async () => {
    await fetchData({ reset: true, skipCache: true });
  }, [fetchData]);

  const fetchMore = useCallback(async () => {
    if (!loading && hasMore) {
      await fetchData({ reset: false });
    }
  }, [fetchData, loading, hasMore]);

  return {
    data,
    loading,
    error,
    cacheMetadata,
    refresh,
    fetchMore,
    hasMore,
  };
}
